/**
 * All Modules Page
 *
 * A comprehensive page that displays all menu items from all modules.
 * Supports two view modes:
 * - Grid: Sitemap-style with groups as cards (matching ModuleOverviewPage styling)
 * - List: Collapsible module sections with items in horizontal flex-wrap
 *
 * Usage: /modules
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Search,
  ChevronDown,
  ChevronRight,
  X,
  Grid3X3,
  List,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  getNavigationItems,
  hasSubMenu,
  type NavigationItem,
  type SubMenuItem,
} from "@/lib/config/navigationConfig";

type ViewMode = "grid" | "list";

// Group accent colors (same as ModuleOverviewPage)
const groupColors: Record<string, string> = {
  "Catalog": "from-cyan-50 to-cyan-100/50 dark:from-cyan-950/30 dark:to-cyan-900/20",
  "Pricing": "from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20",
  "Attributes": "from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20",
  "Warehouses": "from-teal-50 to-teal-100/50 dark:from-teal-950/30 dark:to-teal-900/20",
  "Transactions": "from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20",
  "Receiving": "from-indigo-50 to-indigo-100/50 dark:from-indigo-950/30 dark:to-indigo-900/20",
  "Serial & Batch": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Reports": "from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20",
  "Tools": "from-slate-50 to-slate-100/50 dark:from-slate-950/30 dark:to-slate-900/20",
  "Customers": "from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20",
  "Contacts": "from-sky-50 to-sky-100/50 dark:from-sky-950/30 dark:to-sky-900/20",
  "Loyalty": "from-pink-50 to-pink-100/50 dark:from-pink-950/30 dark:to-pink-900/20",
  "Sales": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Invoicing": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Fulfillment": "from-lime-50 to-lime-100/50 dark:from-lime-950/30 dark:to-lime-900/20",
  "Sales Team": "from-rose-50 to-rose-100/50 dark:from-rose-950/30 dark:to-rose-900/20",
  "Purchasing": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Suppliers": "from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20",
  "General": "from-gray-50 to-gray-100/50 dark:from-gray-950/30 dark:to-gray-900/20",
  "Stock Settings": "from-teal-50 to-teal-100/50 dark:from-teal-950/30 dark:to-teal-900/20",
  "Selling Settings": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Buying Settings": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Print & Email": "from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20",
};

export function AllModulesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Get all modules that have submenus
  const modules = useMemo(() => {
    return getNavigationItems().filter(hasSubMenu);
  }, []);

  // Toggle module collapse (for list view)
  const toggleModule = (moduleName: string) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleName)) {
        next.delete(moduleName);
      } else {
        next.add(moduleName);
      }
      return next;
    });
  };

  // Toggle group collapse (for grid view)
  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Filter modules and items by search query
  const filteredModules = useMemo(() => {
    if (!searchQuery.trim()) return modules;

    const query = searchQuery.toLowerCase();
    return modules
      .map((module) => {
        if (!module.subMenu) return null;

        // Check if module name matches
        const moduleMatches = module.name.toLowerCase().includes(query);

        // Filter groups and items
        const filteredSubMenu = module.subMenu
          .map((group) => ({
            ...group,
            items: group.items.filter(
              (item) =>
                item.name.toLowerCase().includes(query) ||
                group.title.toLowerCase().includes(query)
            ),
          }))
          .filter((group) => group.items.length > 0);

        // If module name matches, return all items; otherwise return filtered
        if (moduleMatches) {
          return module;
        }

        if (filteredSubMenu.length === 0) return null;

        return {
          ...module,
          subMenu: filteredSubMenu,
        };
      })
      .filter(Boolean) as (NavigationItem & { subMenu: NonNullable<NavigationItem["subMenu"]> })[];
  }, [modules, searchQuery]);

  // Count total items
  const totalItems = useMemo(() => {
    return modules.reduce((acc, module) => {
      if (!module.subMenu) return acc;
      return acc + module.subMenu.reduce((groupAcc, group) => groupAcc + group.items.length, 0);
    }, 0);
  }, [modules]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6"
      >
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
              All Modules
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {modules.length} modules · {totalItems} items
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-sm p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={cn(
                "p-2 rounded-xs transition-colors",
                viewMode === "grid"
                  ? "bg-white dark:bg-gray-700 shadow-sm"
                  : "hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={cn(
                "p-2 rounded-xs transition-colors",
                viewMode === "list"
                  ? "bg-white dark:bg-gray-700 shadow-sm"
                  : "hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="mb-10"
      >
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search all modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-11 rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-500 mt-2">
            {filteredModules.reduce(
              (acc, m) => acc + (m.subMenu?.reduce((a, g) => a + g.items.length, 0) || 0),
              0
            )}{" "}
            results for "{searchQuery}"
          </p>
        )}
      </motion.div>

      {/* Content based on view mode */}
      {viewMode === "grid" ? (
        // Grid View - Sitemap style (matching ModuleOverviewPage)
        <div className="space-y-12">
          {filteredModules.map((module, moduleIndex) => (
            <ModuleSectionGrid
              key={module.name}
              module={module}
              index={moduleIndex}
              searchQuery={searchQuery}
              collapsedGroups={collapsedGroups}
              toggleGroup={toggleGroup}
            />
          ))}
        </div>
      ) : (
        // List View - Collapsible sections
        <div className="space-y-6">
          {filteredModules.map((module, moduleIndex) => (
            <ModuleSectionList
              key={module.name}
              module={module}
              index={moduleIndex}
              isCollapsed={collapsedModules.has(module.name)}
              onToggle={() => toggleModule(module.name)}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}

      {/* No Results */}
      {filteredModules.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            No items found matching "{searchQuery}"
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")}>
            Clear search
          </Button>
        </motion.div>
      )}
    </div>
  );
}

// ============================================
// Grid View - Sitemap Style (matching ModuleOverviewPage)
// ============================================

interface ModuleSectionGridProps {
  module: NavigationItem & { subMenu: NonNullable<NavigationItem["subMenu"]> };
  index: number;
  searchQuery: string;
  collapsedGroups: Set<string>;
  toggleGroup: (groupKey: string) => void;
}

function ModuleSectionGrid({ module, index, searchQuery, collapsedGroups, toggleGroup }: ModuleSectionGridProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      {/* Module Header */}
      <div className="flex items-center gap-3 mb-5">
        <div
          className={cn(
            "w-10 h-10 rounded flex items-center justify-center shrink-0 shadow-sm",
            module.color
          )}
        >
          <module.icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {module.name}
        </h2>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {module.subMenu.map((group, groupIndex) => {
          const groupKey = `${module.name}-${group.title}`;
          return (
            <GroupCard
              key={group.title}
              group={group}
              groupIndex={groupIndex}
              searchQuery={searchQuery}
              isCollapsed={collapsedGroups.has(groupKey)}
              onToggle={() => toggleGroup(groupKey)}
            />
          );
        })}
      </div>
    </motion.div>
  );
}

interface GroupCardProps {
  group: { title: string; items: SubMenuItem[] };
  groupIndex: number;
  searchQuery: string;
  isCollapsed: boolean;
  onToggle: () => void;
}

function GroupCard({ group, groupIndex, searchQuery, isCollapsed, onToggle }: GroupCardProps) {
  const groupColor = groupColors[group.title] || groupColors["General"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: groupIndex * 0.05, duration: 0.3 }}
      className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
    >
      {/* Group Header */}
      <button
        onClick={onToggle}
        className={cn(
          "w-full px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between",
          "bg-gradient-to-r",
          groupColor,
          "hover:brightness-95 dark:hover:brightness-110 transition-all"
        )}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
          {group.title}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{group.items.length}</span>
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Group Items */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2">
              {group.items.map((item, itemIndex) => (
                <ItemRow
                  key={item.href}
                  item={item}
                  itemIndex={itemIndex}
                  groupIndex={groupIndex}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface ItemRowProps {
  item: SubMenuItem;
  itemIndex: number;
  groupIndex: number;
  searchQuery: string;
}

function ItemRow({ item, itemIndex, groupIndex, searchQuery }: ItemRowProps) {
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const query = searchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 dark:bg-yellow-800 rounded-md px-0.5">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: groupIndex * 0.05 + itemIndex * 0.02,
        duration: 0.2,
      }}
      className="group"
    >
      <Link
        to={item.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded transition-all duration-200",
          "hover:bg-gray-50 dark:hover:bg-gray-800",
          "hover:scale-[1.01] active:scale-[0.99]"
        )}
      >
        <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors">
          <item.icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
            {highlightText(item.name)}
          </span>
        </div>

        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
    </motion.div>
  );
}

// ============================================
// List View - Collapsible Sections
// ============================================

interface ModuleSectionListProps {
  module: NavigationItem & { subMenu: NonNullable<NavigationItem["subMenu"]> };
  index: number;
  isCollapsed: boolean;
  onToggle: () => void;
  searchQuery: string;
}

function ModuleSectionList({ module, index, isCollapsed, onToggle, searchQuery }: ModuleSectionListProps) {
  const itemCount = module.subMenu.reduce((acc, group) => acc + group.items.length, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden"
    >
      {/* Module Header */}
      <button
        onClick={onToggle}
        className={cn(
          "w-full px-5 py-4 flex items-center gap-4",
          "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        )}
      >
        {/* Module Icon */}
        <div
          className={cn(
            "w-10 h-10 rounded flex items-center justify-center shrink-0 shadow-sm",
            module.color
          )}
        >
          <module.icon className="w-5 h-5 text-white" />
        </div>

        {/* Module Name */}
        <div className="flex-1 text-left">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {module.name}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {itemCount} items
          </p>
        </div>

        {/* Collapse Arrow */}
        {isCollapsed ? (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Module Content */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1">
              {/* Groups */}
              <div className="space-y-4">
                {module.subMenu.map((group) => (
                  <div key={group.title}>
                    {/* Group Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 px-1">
                      {group.title}
                    </h3>

                    {/* Group Items - Flex wrap for horizontal layout */}
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((item) => (
                        <ItemButton
                          key={item.href}
                          item={item}
                          searchQuery={searchQuery}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface ItemButtonProps {
  item: SubMenuItem;
  searchQuery: string;
}

function ItemButton({ item, searchQuery }: ItemButtonProps) {
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const query = searchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 dark:bg-yellow-800 rounded-md px-0.5">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  return (
    <Link
      to={item.href}
      className={cn(
        "inline-flex items-center gap-2 px-3 py-2 rounded transition-all duration-200",
        "bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700",
        "hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-200 dark:hover:border-gray-600",
        "hover:scale-[1.02] active:scale-[0.98]",
        "group"
      )}
    >
      <div className="w-7 h-7 rounded-sm bg-white dark:bg-gray-700 flex items-center justify-center shrink-0 shadow-sm group-hover:shadow transition-shadow">
        <item.icon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
        {highlightText(item.name)}
      </span>
    </Link>
  );
}
